import type { Movimiento } from '../lib/supabase';
import type { EstadoComprobante, Respaldo } from '../types/comprobante';
import { ZONA_OPERACION } from './dateHelpers';

export const HORAS_PLAZO_COMPROBANTE = 72;
export const DIAS_MODO_REGULARIZACION = 7;
const MS_POR_HORA = 3_600_000;

type MovimientoParaExencion = Pick<Movimiento,
  | 'tipo' | 'origen_tipo' | 'afecta_balance'
  | 'notificacion_balance_id' | 'envio_campana_id' | 'saldo_inicial_campana'>;

/** Cara automática de un envío de campaña entre juntas. */
export function esCaraDeEnvio(m: MovimientoParaExencion): boolean {
  return m.envio_campana_id != null;
}

/** Cara que nace de confirmar una notificación de balance con cuota iglesia. */
function esTraspasoDeBalance(m: MovimientoParaExencion): boolean {
  return m.notificacion_balance_id != null;
}

/** Apertura histórica del fondo de una campaña: no es una recaudación nueva. */
function esAperturaDeCampana(m: MovimientoParaExencion): boolean {
  return m.saldo_inicial_campana === true;
}

/**
 * Espejo de public.movimiento_exento_de_comprobante (migración 20261005120100). Cambiar uno obliga a
 * cambiar el otro: traspasos de cuota iglesia generados al confirmar una notificación de balance
 * (los respalda su comprobante), caras automáticas de envíos de campaña (las respalda su origen),
 * aperturas históricas del fondo de una campaña, y donaciones (egreso que no sale de la caja: gasto
 * sin origen o costo de actividad). Un ingreso manual de cuota iglesia no queda exento.
 */
export function movimientoExentoDeComprobante(m: MovimientoParaExencion): boolean {
  const esDonacion = m.tipo === 'egreso'
    && m.afecta_balance === false
    && (m.origen_tipo == null || m.origen_tipo === 'actividad');
  return esTraspasoDeBalance(m) || esCaraDeEnvio(m) || esAperturaDeCampana(m) || esDonacion;
}

export function horasTranscurridas(desde: string, ahora: Date): number {
  return (ahora.getTime() - new Date(desde).getTime()) / MS_POR_HORA;
}

/** A las 72 horas el pendiente pasa a "Vencido". */
export function estaVencido(pendienteDesde: string, ahora: Date): boolean {
  return horasTranscurridas(pendienteDesde, ahora) >= HORAS_PLAZO_COMPROBANTE;
}

/** Con más de 7 días, la entidad entra en modo regularización (mismo corte que la base: `<`). */
export function cuentaParaRegularizacion(pendienteDesde: string, ahora: Date): boolean {
  return horasTranscurridas(pendienteDesde, ahora) > DIAS_MODO_REGULARIZACION * 24;
}

/** "quedan 2 d 5 h", "quedan 5 h", "queda menos de 1 h". Null si ya venció. */
export function textoTiempoRestante(pendienteDesde: string, ahora: Date): string | null {
  const restantes = HORAS_PLAZO_COMPROBANTE - horasTranscurridas(pendienteDesde, ahora);
  if (restantes <= 0) return null;
  if (restantes < 1) return 'queda menos de 1 h';
  const horasEnteras = Math.floor(restantes);
  const dias = Math.floor(horasEnteras / 24);
  const horas = horasEnteras % 24;
  if (dias === 0) return `quedan ${horas} h`;
  return horas === 0 ? `quedan ${dias} d` : `quedan ${dias} d ${horas} h`;
}

const FORMATO_VENCIMIENTO = new Intl.DateTimeFormat('es-CL', {
  timeZone: ZONA_OPERACION,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
});

/** Fecha y hora de Chile en que vence el plazo de 72 horas. */
export function vencimientoEnChile(pendienteDesde: string): string {
  const vence = new Date(new Date(pendienteDesde).getTime() + HORAS_PLAZO_COMPROBANTE * MS_POR_HORA);
  return FORMATO_VENCIMIENTO.format(vence);
}

/**
 * Un movimiento está bajo la exigencia si se registró desde la fecha de entrada en vigor o si alguna
 * vez tuvo comprobante adjunto. La base guarda esa respuesta en el propio movimiento (H20) y nunca la
 * apaga; esta función solo la lee. No compara fechas: la app no conoce la fecha de entrada en vigor.
 * Espejo de la marca que leen la vista `comprobantes_faltantes` y la objeción de §4.
 */
export function movimientoBajoExigencia(m: Pick<Movimiento, 'comprobante_bajo_exigencia'>): boolean {
  return m.comprobante_bajo_exigencia === true;
}

export function reembolsoBajoExigencia(m: Pick<Movimiento, 'reembolso_comprobante_bajo_exigencia'>): boolean {
  return m.reembolso_comprobante_bajo_exigencia === true;
}

/**
 * ¿Ofrece la pantalla objetar este respaldo? Mismo criterio que la RPC de §4: un comprobante adjunto
 * de un movimiento bajo la exigencia (también antes de la fecha de entrada en vigor) que no tome su
 * respaldo de otra operación (traspasos, envíos de campaña, donaciones).
 */
export function respaldoObjetable(
  m: MovimientoParaExencion & Pick<Movimiento,
    'comprobante' | 'comprobante_reembolso' | 'comprobante_bajo_exigencia' | 'reembolso_comprobante_bajo_exigencia'>,
  respaldo: Respaldo,
): boolean {
  if (movimientoExentoDeComprobante(m)) return false;
  return respaldo === 'pago'
    ? m.comprobante != null && movimientoBajoExigencia(m)
    : m.comprobante_reembolso != null && reembolsoBajoExigencia(m);
}

type MovimientoParaEstado = MovimientoParaExencion & Pick<Movimiento,
  | 'created_at' | 'modo_pago_cuota_iglesia' | 'comprobante' | 'comprobante_pendiente_desde'
  | 'comprobante_objecion_motivo' | 'comprobante_bajo_exigencia'>;

export function estadoComprobanteDelPago(m: MovimientoParaEstado, ahora: Date): EstadoComprobante {
  if (m.comprobante) return { tipo: 'adjunto', comprobante: m.comprobante };
  if (m.comprobante_pendiente_desde) {
    const datos = { pendienteDesde: m.comprobante_pendiente_desde, objecionMotivo: m.comprobante_objecion_motivo ?? null };
    return estaVencido(m.comprobante_pendiente_desde, ahora)
      ? { tipo: 'vencido', ...datos }
      : { tipo: 'pendiente', ...datos };
  }
  if (movimientoExentoDeComprobante(m)) {
    if (esCaraDeEnvio(m)) return { tipo: 'exento', motivo: 'envio' };
    if (esAperturaDeCampana(m)) return { tipo: 'exento', motivo: 'apertura' };
    if (!esTraspasoDeBalance(m)) return { tipo: 'exento', motivo: 'donacion' };
    return { tipo: 'exento', motivo: m.modo_pago_cuota_iglesia == null ? 'traspaso' : 'pago_automatico' };
  }
  // Sin comprobante y sin plazo, "marcha blanca" solo vale si el movimiento no está bajo la
  // exigencia: se registró sin comprobante antes de la entrada en vigor y nunca recibió uno.
  if (!movimientoBajoExigencia(m)) return { tipo: 'marcha_blanca' };
  // Bajo la exigencia, sin comprobante, plazo ni exención: la base no lo permite (§1 `CHECK` y §2).
  // Si ocurriera por un dato corrupto, se muestra vencido en vez de ocultarlo como "marcha blanca".
  return { tipo: 'vencido', pendienteDesde: m.created_at ?? ahora.toISOString(), objecionMotivo: null };
}

type MovimientoParaReembolso = Pick<Movimiento,
  | 'reembolsado' | 'comprobante_reembolso' | 'reembolso_comprobante_pendiente_desde'
  | 'reembolso_comprobante_objecion_motivo' | 'reembolso_comprobante_bajo_exigencia' | 'created_at'>;

/** Null si el gasto no está reembolsado: no hay respaldo de reembolso que mostrar. */
export function estadoComprobanteDelReembolso(m: MovimientoParaReembolso, ahora: Date): EstadoComprobante | null {
  if (!m.reembolsado) return null;
  if (m.comprobante_reembolso) return { tipo: 'adjunto', comprobante: m.comprobante_reembolso };
  if (m.reembolso_comprobante_pendiente_desde) {
    const datos = {
      pendienteDesde: m.reembolso_comprobante_pendiente_desde,
      objecionMotivo: m.reembolso_comprobante_objecion_motivo ?? null,
    };
    return estaVencido(m.reembolso_comprobante_pendiente_desde, ahora)
      ? { tipo: 'vencido', ...datos }
      : { tipo: 'pendiente', ...datos };
  }
  if (!reembolsoBajoExigencia(m)) return { tipo: 'marcha_blanca' };
  return { tipo: 'vencido', pendienteDesde: m.created_at ?? ahora.toISOString(), objecionMotivo: null };
}

/** Un pendiente o vencido que nació de una objeción: se corrige reemplazando el comprobante. */
export function estaObjetado(estado: EstadoComprobante): boolean {
  return (estado.tipo === 'pendiente' || estado.tipo === 'vencido') && estado.objecionMotivo !== null;
}

// Map y no un objeto literal: el motivo "Otro" es texto libre y una clave como `constructor` no debe
// resolver a una propiedad heredada.
const INDICACION_POR_MOTIVO: ReadonlyMap<string, string> = new Map([
  ['Ilegible', 'El comprobante no se lee bien; sube una foto más nítida.'],
  ['El monto no coincide', 'El monto del comprobante no coincide con el movimiento; revisa que sea el correcto.'],
  ['No es un comprobante', 'El archivo no parece un comprobante; adjunta la boleta, factura o recibo.'],
]);

/**
 * Lo que se le muestra a quien debe corregir un comprobante objetado: el motivo en tono amable y cómo
 * corregirlo. Con el motivo "Otro" es el texto que escribió quien objetó, tal cual. La misma tabla
 * existe en SQL (§6 `indicacion_de_objecion`) para el aviso diario; §19 verifica que digan lo mismo.
 */
export function indicacionDeObjecion(motivo: string): string {
  const limpio = motivo.trim();
  return INDICACION_POR_MOTIVO.get(limpio) ?? limpio;
}
